import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from 'constants/ApiConstant';
import { AUTH_TOKEN } from 'constants/AuthConstant';

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  events: [],
  currentEvent: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  }
};

// Create Event
export const createEvent = createAsyncThunk(
  'event/createEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}admin/create-event`, eventData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error creating event';
      return rejectWithValue(errorMessage);
    }
  }
);

// Update Event
export const updateEvent = createAsyncThunk(
  'event/updateEvent',
  async ({ id, eventData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}admin/update-event/${id}`, eventData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error updating event';
      return rejectWithValue(errorMessage);
    }
  }
);

// Get Single Event
export const getEvent = createAsyncThunk(
  'event/getEvent',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}admin/view-event/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error fetching event';
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete Event
export const deleteEvent = createAsyncThunk(
  'event/deleteEvent',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}admin/delete-event/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error deleting event';
      return rejectWithValue(errorMessage);
    }
  }
);

// List Events
export const listEvents = createAsyncThunk(
  'event/listEvents',
  async (params = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}admin/list-event`, {
        params,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
        }
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error fetching events';
      return rejectWithValue(errorMessage);
    }
  }
);

export const eventSlice = createSlice({
  name: 'event',
  initialState,
  reducers: {
    showEventMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideEventMessage: (state) => {
      state.message = '';
      state.showMessage = false;
    },
    setCurrentEvent: (state, action) => {
      state.currentEvent = action.payload;
    },
    clearCurrentEvent: (state) => {
      state.currentEvent = null;
    },
    showLoading: (state) => {
      state.loading = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.showMessage = true;
        state.events.unshift(action.payload.data);
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.showMessage = true;
        state.events = state.events.map(event => 
          event._id === action.payload.data._id ? action.payload.data : event
        );
        if (state.currentEvent?._id === action.payload.data._id) {
          state.currentEvent = action.payload.data;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(getEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEvent = action.payload.data;
      })
      .addCase(getEvent.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.showMessage = true;
        state.events = state.events.map(event => 
          event._id === action.payload.data._id ? action.payload.data : event
        );
        if (state.currentEvent?._id === action.payload.data._id) {
          state.currentEvent = null;
        }
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(listEvents.pending, (state) => {
        state.loading = true;
      })
      .addCase(listEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(listEvents.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      });
  }
});

export const {
  showEventMessage,
  hideEventMessage,
  setCurrentEvent,
  clearCurrentEvent,
  showLoading
} = eventSlice.actions;

export default eventSlice.reducer;
